import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

interface OgData {
  url: string;
  upstream_status?: number;
  upstream_user_agent?: string;
  title: string | null;
  description: string | null;
  image: string | null;
  site_name: string | null;
  type: string | null;
  og: Record<string, string>;
  twitter: Record<string, string>;
  error?: string;
}

const USER_AGENT_OPTIONS: { label: string; value: string }[] = [
  { label: "Default (BoardoBot)", value: "" },
  {
    label: "Facebook (facebookexternalhit)",
    value:
      "facebookexternalhit/1.1 (+http://www.facebook.com/externalhit_uatext.php)",
  },
  { label: "Facebot", value: "Facebot" },
  {
    label: "Meta external agent",
    value:
      "meta-externalagent/1.1 (+https://developers.facebook.com/docs/sharing/webmasters/crawler)",
  },
  { label: "Twitterbot", value: "Twitterbot" },
  { label: "LinkedInBot", value: "LinkedInBot" },
  {
    label: "WhatsApp",
    value: "WhatsApp/2.0",
  },
  {
    label: "Googlebot",
    value:
      "Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)",
  },
];

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function FacebookPreview({ data }: { data: OgData }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-300 bg-white">
      {data.image && (
        <div className="aspect-[1.91/1] w-full overflow-hidden bg-gray-100">
          <img
            src={data.image}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className="bg-gray-50 px-3 py-2.5">
        <p className="text-xs uppercase text-gray-500">
          {extractDomain(data.url)}
        </p>
        <p className="mt-0.5 line-clamp-1 text-sm font-semibold text-gray-900">
          {data.title || "No title"}
        </p>
        {data.description && (
          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
            {data.description}
          </p>
        )}
      </div>
    </div>
  );
}

function TwitterPreview({ data }: { data: OgData }) {
  const twitterCard = data.twitter.card || "summary";
  const isLargeImage = twitterCard === "summary_large_image";
  const title = data.twitter.title || data.title;
  const description = data.twitter.description || data.description;
  const image = data.twitter.image || data.image;

  if (isLargeImage) {
    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        {image && (
          <div className="aspect-[2/1] w-full overflow-hidden bg-gray-100">
            <img
              src={image}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
        )}
        <div className="px-3 py-2.5">
          <p className="line-clamp-1 text-sm font-normal text-gray-900">
            {title || "No title"}
          </p>
          {description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
              {description}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            {extractDomain(data.url)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {image && (
        <div className="flex h-32 w-32 shrink-0 items-center overflow-hidden bg-gray-100">
          <img
            src={image}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className="flex flex-col justify-center px-3 py-2">
        <p className="line-clamp-1 text-sm font-normal text-gray-900">
          {title || "No title"}
        </p>
        {description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
            {description}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-400">
          {extractDomain(data.url)}
        </p>
      </div>
    </div>
  );
}

function LinkedInPreview({ data }: { data: OgData }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-300 bg-white">
      {data.image && (
        <div className="aspect-[1.91/1] w-full overflow-hidden bg-gray-100">
          <img
            src={data.image}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
      )}
      <div className="px-3 py-2.5">
        <p className="line-clamp-2 text-sm font-semibold text-gray-900">
          {data.title || "No title"}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {extractDomain(data.url)}
        </p>
      </div>
    </div>
  );
}

function RawTagsTable({
  label,
  tags,
}: {
  label: string;
  tags: Record<string, string>;
}) {
  const entries = Object.entries(tags);
  if (entries.length === 0) return null;

  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-gray-700">{label}</h4>
      <table className="w-full text-sm">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-gray-100">
              <td className="w-40 py-1.5 pr-3 font-mono text-xs text-gray-500">
                {key}
              </td>
              <td className="break-all py-1.5 text-gray-900">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminOgPreviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [url, setUrl] = useState(searchParams.get("url") || "");
  const [userAgent, setUserAgent] = useState(searchParams.get("ua") || "");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OgData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = async (targetUrl: string, ua: string) => {
    if (!targetUrl.trim()) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const token = localStorage.getItem("auth_token");
      const qs = new URLSearchParams({ url: targetUrl.trim() });
      if (ua.trim()) qs.set("user_agent", ua.trim());
      const res = await fetch(`/admin/og_preview?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || "Failed to fetch");
      } else {
        setData(json);
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;

    const next: Record<string, string> = { url: url.trim() };
    if (userAgent.trim()) next.ua = userAgent.trim();
    setSearchParams(next, { replace: true });
    fetchPreview(url, userAgent);
  };

  useEffect(() => {
    const initialUrl = searchParams.get("url");
    const initialUa = searchParams.get("ua") || "";
    if (initialUrl) {
      fetchPreview(initialUrl, initialUa);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link
        to="/admin"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        &larr; Admin valdymo skydas
      </Link>
      <h1 className="text-2xl font-bold text-gray-900">OG Preview</h1>
      <p className="mt-1 text-sm text-gray-500">
        Preview how a link will look when shared on social media
      </p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-3">
        <div className="flex gap-3">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://boardo.lt/listings/123"
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            type="submit"
            disabled={loading || !url.trim()}
            className="rounded-lg bg-primary-800 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-900 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Preview"}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500">
            Simulate user agent:
          </label>
          <select
            value={
              USER_AGENT_OPTIONS.some((o) => o.value === userAgent)
                ? userAgent
                : "__custom__"
            }
            onChange={(e) => {
              const v = e.target.value;
              setUserAgent(v === "__custom__" ? userAgent : v);
            }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs"
          >
            {USER_AGENT_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          <input
            type="text"
            value={userAgent}
            onChange={(e) => setUserAgent(e.target.value)}
            placeholder="Custom UA string (overrides above)"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-mono"
          />
        </div>
      </form>

      {loading && (
        <div className="mt-8 flex justify-center">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-8">
          {data.upstream_status !== undefined && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                data.upstream_status >= 200 && data.upstream_status < 300
                  ? "border-green-200 bg-green-50 text-green-800"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
            >
              Upstream returned <strong>HTTP {data.upstream_status}</strong>
              {data.upstream_user_agent && (
                <span className="ml-2 font-mono text-xs opacity-75">
                  (UA: {data.upstream_user_agent})
                </span>
              )}
            </div>
          )}
          {/* Preview cards */}
          <div className="grid gap-8 lg:grid-cols-3">
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                Facebook
              </h3>
              <div className="max-w-md">
                <FacebookPreview data={data} />
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                X (Twitter)
              </h3>
              <div className="max-w-md">
                <TwitterPreview data={data} />
              </div>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                LinkedIn
              </h3>
              <div className="max-w-md">
                <LinkedInPreview data={data} />
              </div>
            </div>
          </div>

          {/* Raw tags */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-base font-semibold text-gray-900">
              Raw Meta Tags
            </h3>
            <div className="space-y-6">
              <RawTagsTable label="Open Graph (og:*)" tags={data.og} />
              <RawTagsTable label="Twitter Card (twitter:*)" tags={data.twitter} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
